// Single source of truth for everything pricing-related in the UI.
//
// PRICING_MODEL controls which presentation the pricing surfaces use:
//   - 'subscription' (default): the current monthly plans, exactly as before.
//   - 'passes': time-boxed one-off access passes.
//
// The default stays 'subscription' unless REACT_APP_PRICING_MODEL is explicitly
// set to 'passes'. Pass checkout is implemented (see the pass CTA call sites and
// PassCheckoutDialog); the flag only selects which pricing surface is shown.
export type PricingModel = 'subscription' | 'passes';

const requestedPricingModel = process.env.REACT_APP_PRICING_MODEL;

export const PRICING_MODEL: PricingModel =
  requestedPricingModel === 'passes' ? 'passes' : 'subscription';

export const isPassesModel = PRICING_MODEL === 'passes';

export interface SubscriptionPlan {
  id: string;
  // Price in euros, including 21% btw.
  price: number;
  recommended: boolean;
  i18nKey: string;
}

// Current monthly plans. Keep the numeric prices here only.
export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: 'free',
    price: 0,
    recommended: false,
    i18nKey: 'landing.pricing.plans.free',
  },
  {
    id: 'level_bundle',
    price: 9.99,
    recommended: true,
    i18nKey: 'landing.pricing.plans.level_bundle',
  },
  {
    id: 'full_access',
    price: 12.99,
    recommended: false,
    i18nKey: 'landing.pricing.plans.full_access',
  },
];

export const getSubscriptionPlan = (id: string | null | undefined): SubscriptionPlan | undefined =>
  SUBSCRIPTION_PLANS.find((plan) => plan.id === id);

export type PassDuration = '24h' | '1_week' | '2_weeks' | '1_month';

export interface PassTier {
  id: string;
  duration: PassDuration;
  // Price in euros, including 21% btw.
  price: number;
  recommended: boolean;
  i18nKey: string;
}

// PROPOSED PRICES, pending the owner's decision. Change a price here and every
// pricing surface follows; do not repeat these numbers in components.
// 4,99 / 14,99 / 24,99 / 34,99 euro incl. 21% btw.
export const PASS_TIERS: PassTier[] = [
  {
    id: 'pass_24h',
    duration: '24h',
    price: 4.99,
    recommended: false,
    i18nKey: 'landing.pricing.passes.tiers.pass_24h',
  },
  {
    id: 'pass_1_week',
    duration: '1_week',
    price: 14.99,
    recommended: false,
    i18nKey: 'landing.pricing.passes.tiers.pass_1_week',
  },
  {
    id: 'pass_2_weeks',
    duration: '2_weeks',
    price: 24.99,
    recommended: false,
    i18nKey: 'landing.pricing.passes.tiers.pass_2_weeks',
  },
  {
    id: 'pass_1_month',
    duration: '1_month',
    price: 34.99,
    recommended: true,
    i18nKey: 'landing.pricing.passes.tiers.pass_1_month',
  },
];

export const getPassTier = (id: string | null | undefined): PassTier | undefined =>
  PASS_TIERS.find((tier) => tier.id === id);

export const PASS_DURATION_LABEL_KEYS: Record<PassDuration, string> = {
  '24h': 'landing.pricing.passes.durations.24h',
  '1_week': 'landing.pricing.passes.durations.1_week',
  '2_weeks': 'landing.pricing.passes.durations.2_weeks',
  '1_month': 'landing.pricing.passes.durations.1_month',
};

// The free tier stays model-agnostic: one free practice exam per section, one attempt each.
export const FREE_TIER_I18N_KEY = 'landing.pricing.plans.free';

// Formats a euro amount the same way the existing checkout copy did (e.g. 9.99 -> 9,99).
export const formatEuroAmount = (value: number): string =>
  value.toFixed(2).replace('.', ',');
